import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Inbox, Loader2, CheckCircle2, Reply, ExternalLink, Undo2 } from 'lucide-react';
import { SmsThreadDialog } from '../custom/SmsThreadDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _fmt = (iso) => {
  try { return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso || ''; }
};

/** Reply Inbox — every inbound customer SMS across the team, flagged
 *  answered/unanswered so managers can make sure nothing slips. */
const ReplyInboxCard = ({ domainId = null, days = 14 }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('unanswered');
  const [busyId, setBusyId] = useState(null);
  const [threadPhone, setThreadPhone] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    p.set('days', String(days));
    p.set('status', status);
    axios
      .get(`${API}/analytics/sms-replies?${p.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [domainId, days, status]);

  useEffect(() => { load(); }, [load]);

  const setHandled = async (r, handled) => {
    setBusyId(r.id);
    try {
      await axios.patch(`${API}/analytics/sms-replies/${r.id}/handled`, { handled });
      toast.success(handled ? 'Marked handled' : 'Moved back to unanswered');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading reply inbox…
      </div>
    );
  }
  if (!data || data.enabled === false) return null;

  const replies = data.replies || [];

  return (
    <div className="rounded-xl border border-emerald-200 bg-white p-4" data-testid="reply-inbox-card">
      <div className="flex items-center flex-wrap gap-2 mb-3">
        <Inbox className="w-4 h-4 text-emerald-700" />
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-700">Reply Inbox</p>
        <span
          className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${data.unanswered_count > 0 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}
          data-testid="reply-inbox-unanswered-count"
        >
          {data.unanswered_count} unanswered
        </span>
        <div className="ml-auto flex rounded-lg border border-slate-200 overflow-hidden text-[11px] font-semibold">
          {['unanswered', 'all'].map((s) => (
            <button
              key={s}
              onClick={() => { setStatus(s); }}
              className={`px-2.5 py-1 capitalize ${status === s ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              data-testid={`reply-inbox-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {replies.length === 0 ? (
        <p className="text-xs text-slate-500 py-2" data-testid="reply-inbox-empty">
          {status === 'unanswered' ? 'No unanswered replies — the team is on top of it.' : `No replies in the last ${data.days} days.`}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {replies.map((r) => (
            <li key={r.id} className="py-2.5 flex flex-col sm:flex-row sm:items-start gap-2" data-testid={`reply-inbox-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-2">
                  <button onClick={() => setThreadPhone(r.from_phone)}
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 inline-flex items-center gap-0.5 hover:bg-emerald-200 hover:underline underline-offset-2"
                    title="View full conversation"
                    data-testid={`reply-inbox-thread-${r.id}`}>
                    <Reply className="w-2.5 h-2.5" /> {r.from_phone}
                  </button>
                  <span className="text-[10px] text-slate-400 tabular-nums">{_fmt(r.received_at)}</span>
                  {r.answered ? (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 inline-flex items-center gap-0.5">
                      <CheckCircle2 className="w-2.5 h-2.5" />
                      {r.answered_via === 'handled' ? `handled${r.handled_by ? ` · ${String(r.handled_by).split('@')[0]}` : ''}` : 'replied'}
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">needs reply</span>
                  )}
                  {r.site && r.site !== 'Unassigned' && (
                    <span className="text-[10px] text-slate-400">{r.site}</span>
                  )}
                </div>
                <p className="text-xs text-slate-800 mt-1 whitespace-pre-wrap">{r.content}</p>
                {(r.sender_name || r.original_content) && (
                  <p className="text-[10px] text-slate-400 mt-0.5 truncate">
                    in reply to {r.sender_name ? `${r.sender_name}'s SMS` : 'our SMS'}
                    {r.original_content ? `: “${r.original_content}”` : ''}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {r.matched_call_id && (
                  <button
                    onClick={() => navigate(`/calls/${r.matched_call_id}`)}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                    data-testid={`reply-inbox-open-call-${r.id}`}
                  >
                    <ExternalLink className="w-3 h-3" /> Open call
                  </button>
                )}
                {r.answered_via === 'handled' ? (
                  <button
                    onClick={() => setHandled(r, false)}
                    disabled={busyId === r.id}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                    data-testid={`reply-inbox-unhandle-${r.id}`}
                  >
                    <Undo2 className="w-3 h-3" /> Unhandle
                  </button>
                ) : !r.answered ? (
                  <button
                    onClick={() => setHandled(r, true)}
                    disabled={busyId === r.id}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    data-testid={`reply-inbox-handle-${r.id}`}
                  >
                    <CheckCircle2 className="w-3 h-3" /> Mark handled
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      <SmsThreadDialog phone={threadPhone} onClose={() => setThreadPhone(null)} />
    </div>
  );
};

export default ReplyInboxCard;
